import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Bell, Smartphone, Share, Plus } from 'lucide-react';
import { subscribePush, isPushSupported } from '@/lib/pushNotifications';
import { toast } from 'sonner';

/**
 * Shown ONCE after first login — proposes installation + push notifications.
 * Detects iOS to show alternative Share → Add to Home Screen instructions.
 */
export function FirstLoginModal({ user }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const isStandalone = typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true);
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

  useEffect(() => {
    if (!user || !user.id) return;
    const key = `lb_first_login_seen_${user.id}`;
    if (localStorage.getItem(key)) return;
    if (isStandalone) { localStorage.setItem(key, '1'); return; }
    // Delay a bit so it doesn't overlap other UI
    const t = setTimeout(() => setOpen(true), 900);
    const beforeInstall = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener('beforeinstallprompt', beforeInstall);
    return () => { clearTimeout(t); window.removeEventListener('beforeinstallprompt', beforeInstall); };
  }, [user, isStandalone]);

  const close = () => {
    if (user?.id) localStorage.setItem(`lb_first_login_seen_${user.id}`, '1');
    setOpen(false);
  };

  const install = async () => {
    if (!prompt) {
      if (isIOS) {
        toast.info('Utilisez le bouton Partager ⬆ puis « Sur l\'écran d\'accueil »', { duration: 6000 });
      } else {
        toast.info("Votre navigateur ne propose pas l'installation directe pour l'instant.");
      }
      return;
    }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') {
      localStorage.setItem('lb_pwa_installed', '1');
      toast.success('LoyerBook installé !');
    }
    setPrompt(null);
  };

  const enableNotifs = async () => {
    if (!isPushSupported()) {
      toast.info("Les notifications ne sont pas supportées sur ce navigateur.");
      return;
    }
    const res = await subscribePush();
    if (res.ok) toast.success('Notifications activées');
    else if (res.reason === 'denied') toast.error('Notifications refusées dans les réglages du navigateur');
    else toast.info('Notifications non activées');
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent data-testid="first-login-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-[#1677FF]" />
            Installez LoyerBook
          </DialogTitle>
          <DialogDescription>
            Retrouvez LoyerBook depuis votre écran d&apos;accueil et recevez des notifications pour vos paiements.
          </DialogDescription>
        </DialogHeader>

        {isIOS ? (
          <div className="space-y-3 text-sm text-slate-700">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
              <div className="font-semibold text-[#0D1B2A] mb-2">Sur iPhone / iPad :</div>
              <ol className="space-y-1.5 pl-4 list-decimal text-xs">
                <li>Appuyez sur le bouton <Share className="inline w-3 h-3" /> <strong>Partager</strong> en bas de Safari</li>
                <li>Faites défiler et sélectionnez <strong><Plus className="inline w-3 h-3" /> Sur l&apos;écran d&apos;accueil</strong></li>
                <li>Confirmez avec <strong>Ajouter</strong></li>
              </ol>
            </div>
          </div>
        ) : (
          <div className="text-sm text-slate-600">
            Cliquez sur « Installer maintenant » : LoyerBook apparaîtra sur votre écran d&apos;accueil comme une vraie application, avec des notifications push.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
          <Button data-testid="first-modal-notifs" variant="outline" onClick={enableNotifs} className="justify-start">
            <Bell className="w-4 h-4 mr-2 text-[#1677FF]" /> Activer les notifications
          </Button>
          {!isIOS && (
            <Button data-testid="first-modal-install" onClick={install} className="bg-[#1677FF] hover:bg-[#0E62DB]">
              <Smartphone className="w-4 h-4 mr-2" /> Installer maintenant
            </Button>
          )}
        </div>

        <DialogFooter>
          <button
            onClick={close}
            data-testid="first-modal-later"
            className="text-xs text-slate-500 hover:text-[#0D1B2A] mx-auto"
          >
            Plus tard
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
